import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Plane, Clock, Target, Award, Camera, Ruler, Building2, ScanLine, Users, ShieldCheck, Mountain, Zap } from "lucide-react";
import Reveal from "@/components/landing/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const STAT_ICONS = { plane: Plane, clock: Clock, target: Target, award: Award, camera: Camera, ruler: Ruler, building: Building2, scan: ScanLine, users: Users, shield: ShieldCheck, mountain: Mountain, zap: Zap };

function Counter({ value }) {
  const num = parseFloat(String(value).replace(/[^\d.]/g, ""));
  const isNum = !isNaN(num) && String(value).match(/\d/);
  const [display, setDisplay] = useState(isNum ? 0 : value);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!isNum) { setDisplay(value); return; }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !started.current) {
        started.current = true;
        const duration = 1400;
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setDisplay(Math.round(num * eased));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [num, isNum, value]);

  return <span ref={ref}>{isNum ? display.toLocaleString("de-DE") : display}</span>;
}

export default function Stats() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/site-stats`).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);

  if (items.length === 0) return null;

  return (
    <section data-testid="stats-band" className="border-b border-white/10 bg-[#0D0D0F]">
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-16 lg:py-20">
        <Reveal>
          <p className="font-tac text-xs tracking-[0.3em] text-[#F5B301]">DROPS TFR // IN ZAHLEN</p>
        </Reveal>
        <div className="mt-10 grid grid-cols-2 gap-y-12 lg:grid-cols-4">
          {items.map((it, i) => {
            const Icon = STAT_ICONS[it.icon] || null;
            return (
              <Reveal key={it.id} delay={i * 0.1}>
                <div data-testid={`stat-${it.id}`} className="border-l border-white/10 pl-5">
                  {Icon && <Icon className="mb-4 h-6 w-6 text-[#F5B301]" strokeWidth={1.5} />}
                  <p className="font-display text-5xl font-black tracking-tighter text-white lg:text-6xl">
                    <Counter value={it.value} />
                    {it.suffix ? <span className="text-[#F5B301]">{it.suffix}</span> : null}
                  </p>
                  <p className="mt-3 font-tac text-[11px] uppercase tracking-[0.25em] text-white/50">{it.label}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
